/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import {
  ChevronDownIconOutline,
  ChevronLeftIconOutline,
  ChevronRightIconOutline,
  ChevronUpIconOutline,
} from '../icons';

/**
 * Types
 */
type IconButtonArrayProps = {
  /** Content displayed in the icon button array */
  children: React.ReactNode;
  /** Orientation of the icon button array */
  orientation?: 'vertical' | 'horizontal';
  /** Whether the icon button array is floating */
  isFloating?: boolean;
  /** Size of the icon button array */
  size?: 'small' | 'medium' | 'large';
  /** Additional class name*/
  className?: string;
};

type CollapsibleButtonWrapperProps = {
  /** Icon buttons to be displayed in the collapsible button wrapper */
  children:
    | React.ReactElement<React.ComponentProps<typeof CleanIconButton>>
    | React.ReactElement<React.ComponentProps<typeof CleanIconButton>>[];
};

const CollapsibleButtonWrapper: React.FC<CollapsibleButtonWrapperProps> = ({
  children,
}) => {
  return <>{children}</>;
};
CollapsibleButtonWrapper.displayName = 'CollapsibleButtonWrapper';

export const IconButtonArrayComponent = <T extends React.ElementType = 'div'>({
  children,
  as,
  isFloating = false,
  orientation = 'horizontal',
  size = 'medium',
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, IconButtonArrayProps>) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const classes = classNames('ndl-icon-btn-array', {
    'n-flex-row': orientation === 'horizontal',
    'n-flex-col': orientation === 'vertical',
    'ndl-array-floating': isFloating,
    [`ndl-${size}`]: size,
    [className!]: className,
  });

  const Component: React.ElementType = as || 'div';

  const applySizeProp = (child: React.ReactElement) => {
    if (
      React.isValidElement<React.ComponentProps<typeof CleanIconButton>>(
        child,
      ) &&
      child.type === CleanIconButton
    ) {
      return React.cloneElement(child, {
        className: classNames(child.props.className, 'ndl-grouped'),
        size: child.props.size || size,
      });
    }
    return child;
  };

  const childrenArray = React.Children.toArray(
    children,
  ) as React.ReactElement[];

  const fixedButtons = childrenArray.filter(
    (child) =>
      !React.isValidElement(child) ||
      (child.type as React.ComponentType).displayName !==
        'CollapsibleButtonWrapper',
  );

  const fixedButtonsWithProps = React.Children.map(fixedButtons, applySizeProp);

  const collapsibleButtonWrapper = childrenArray.find(
    (child) =>
      React.isValidElement(child) &&
      (child.type as React.ComponentType).displayName ===
        'CollapsibleButtonWrapper',
  );

  const collapsibleButtonsWithProps = collapsibleButtonWrapper
    ? React.Children.map(
        (
          collapsibleButtonWrapper as React.ReactElement<CollapsibleButtonWrapperProps>
        ).props.children,
        applySizeProp,
      )
    : null;

  const renderChevronIcon = () => {
    if (orientation === 'horizontal') {
      return isCollapsed ? (
        <ChevronRightIconOutline />
      ) : (
        <ChevronLeftIconOutline />
      );
    }
    return isCollapsed ? <ChevronDownIconOutline /> : <ChevronUpIconOutline />;
  };

  return (
    <Component
      role="group"
      className={classes}
      ref={ref}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {fixedButtonsWithProps}
      {collapsibleButtonsWithProps && (
        <>
          {!isCollapsed && collapsibleButtonsWithProps}
          <CleanIconButton
            className="ndl-grouped"
            onClick={() => {
              setIsCollapsed((prev) => !prev);
            }}
            size={size}
            description={isCollapsed ? 'Show more' : 'Show less'}
            tooltipProps={{
              root: {
                shouldCloseOnReferenceClick: true,
              },
            }}
            htmlAttributes={{
              'aria-expanded': !isCollapsed,
            }}
          >
            {renderChevronIcon()}
          </CleanIconButton>
        </>
      )}
    </Component>
  );
};

const IconButtonArray = Object.assign(IconButtonArrayComponent, {
  CollapsibleButtonWrapper: CollapsibleButtonWrapper,
});

export { IconButtonArray };
